/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { Box, Grid, Checkbox, Chip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import useQueryParams from "../../../hooks/useQueryParams";
import { isEmptyObj } from "../../../utils/helper";
import InputField from "../../../components/common/new/InputField/InputField";

const DIVISION_OPTIONS = [
  { value: "1", entityTitle: "MD" },
  { value: "2", entityTitle: "WD" },
  { value: "37", entityTitle: "YMD" },
  { value: "3", entityTitle: "YWD" },
  { value: "52", entityTitle: "FD(B)" },
  { value: "53", entityTitle: "FD(G)" },
];

const initialFormValue = { dateFrom: "", dateTo: "" };

function AdvancedSearchAttendance({ formRef }) {
  const [initialAdvanceFormValue, setInitialAdvanceFormValue] =
    useState(initialFormValue);
  const [selectedDivision, setSelectedDivision] = useState([]);
  const [dateFromValue, setDateFromValue] = useState(null);
  const [dateToValue, setDateToValue] = useState(null);
  const { getQueryObject, getQueryParams, urlSearchParams } = useQueryParams();
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const queryObject = getQueryObject();
    if (!isEmptyObj(queryObject)) {
      const query = getQueryParams(queryObject)[0];

      // Set date values if they exist in query
      if (query.dateFrom) {
        setDateFromValue(dayjs(query.dateFrom));
      }
      if (query.dateTo) {
        setDateToValue(dayjs(query.dateTo));
      }

      // Initialize selected divisions from URL if present
      if (query.divisionIds) {
        const divisionIdsArray = Array.isArray(query.divisionIds)
          ? query.divisionIds
          : query.divisionIds.split(",");
        const selectedOptions = DIVISION_OPTIONS.filter((option) =>
          divisionIdsArray.includes(option.value)
        );
        setSelectedDivision(selectedOptions);
      }
    } else {
      setInitialAdvanceFormValue(initialFormValue);
      setSelectedDivision([]);
      setDateFromValue(null);
      setDateToValue(null);
    }
  }, [urlSearchParams.toString()]);

  const handleDateFromChange = (newValue) => {
    setDateFromValue(newValue);

    // ✅ If dateTo exceeds 11 months from dateFrom, reset dateTo
    if (dateToValue && newValue) {
      const maxDateTo = dayjs(newValue).add(11, "month");
      const today = dayjs();
      const actualMaxDate = maxDateTo.isBefore(today) ? maxDateTo : today;

      if (dayjs(dateToValue).isAfter(actualMaxDate)) {
        setDateToValue(null);
      }
    }
  };

  const handleDateToChange = (newValue) => {
    setDateToValue(newValue);
  };

  // ✅ Calculate max date for dateTo (11 months ahead OR current date)
  const getMaxDateTo = () => {
    if (dateFromValue) {
      const maxDateTo = dayjs(dateFromValue).add(11, "month"); // 11 months ahead (total 12 months including dateFrom)
      const today = dayjs();

      // Return whichever is earlier: 11 months ahead OR current date
      return maxDateTo.isBefore(today) ? maxDateTo : today;
    }
    return dayjs(); // Current date as default
  };

  return (
    <Box>
      <Box sx={{ width: "100%" }}>
        <Grid container rowSpacing={4} columnSpacing={5}>
          {/* Date From */}
          <Grid item xs={12} sm={6} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Month From"
                views={["month", "year"]}
                value={dateFromValue}
                disableFuture
                onChange={handleDateFromChange}
                renderInput={(props) => <InputField {...props} />}
              />
            </LocalizationProvider>
            {dateFromValue && (
              <input
                type="hidden"
                name="dateFrom"
                value={dayjs(dateFromValue).format("YYYY-MM-DD")}
              />
            )}
          </Grid>

          {/* Date To */}
          <Grid item xs={12} sm={6} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Month To"
                views={["month", "year"]}
                value={dateToValue}
                disableFuture
                minDate={dateFromValue}
                maxDate={getMaxDateTo()}
                onChange={handleDateToChange}
                renderInput={(props) => <InputField {...props} />}
              />
            </LocalizationProvider>
            {dateToValue && (
              <input
                type="hidden"
                name="dateTo"
                value={dayjs(dateToValue).format("YYYY-MM-DD")}
              />
            )}
          </Grid>

          {/* Division Dropdown */}
          <Grid item xs={12} sm={12} md={4}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={DIVISION_OPTIONS}
              getOptionLabel={(option) => option.entityTitle}
              value={selectedDivision}
              onChange={(event, newValue) => {
                setSelectedDivision(newValue);
              }}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              sx={{
                "& fieldset": {
                  borderRadius: "8px",
                },
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                    size="small"
                  />
                  {option.entityTitle}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.entityTitle}
                    size="small"
                    sx={{
                      height: "24px",
                      fontSize: "12px",
                      margin: "2px",
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Divisions"
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": {
                      minHeight: "40px",
                      fontSize: "14px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                    },
                  }}
                />
              )}
            />
            {selectedDivision.length > 0 && (
              <input
                type="hidden"
                name="divisionIds"
                value={selectedDivision.map((d) => d.value).join(",")}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default AdvancedSearchAttendance;
